import { useCallback, useEffect, useRef } from "react";
import { SOCKET_RETRY_DELAY_MS } from "../portalChat.constants";
import io from "socket.io-client";
import { getBackendUrl } from "@Utils";
import type { ChatMessage } from "../api/portalChat.api";

/**
 * Server→client payloads of the `/portal-chat` namespace, named so the `socket.on` listeners below
 * can annotate the payload they forward. socket.io's `io()` returns the untyped `Socket`, whose
 * `DefaultEventsMap` types every listener `(...args: any[]) => void`; naming the payload on the
 * listener is what stops that `any` from reaching the consumer handlers.
 *
 * Each shape is the one the server emitter builds verbatim — see packages/server
 * portalChat.messages.service.ts / portalChat.conversations.service.ts / portalChat.realtime.ts,
 * where every `emitPortalChatEvent({ event, payload })` call site constructs exactly these fields.
 */
type MessagePayload = { conversationId: string; message: ChatMessage };
type MessageDeletedPayload = { conversationId: string; messageId: string };
type ReactionPayload = { conversationId: string; messageId: string; reactions: { emoji: string; users: string[] }[] };
type ReadPayload = { conversationId: string; userId: string; lastReadDate: number };
type ConversationPayload = { conversationId: string };
type TypingPayload = { conversationId: string; userId: string; isTyping: boolean };

export type PortalChatSocketEvents = {
  onMessageNew?: (payload: MessagePayload) => void;
  onMessageEdited?: (payload: MessagePayload) => void;
  onMessageDeleted?: (payload: MessageDeletedPayload) => void;
  onReactionUpdated?: (payload: ReactionPayload) => void;
  onReadUpdated?: (payload: ReadPayload) => void;
  onConversationUpdated?: (payload: ConversationPayload) => void;
  onMemberRemoved?: (payload: ConversationPayload) => void;
  onTyping?: (payload: TypingPayload) => void;
  onConnectionChange?: (connected: boolean) => void;
};

/**
 * Extra connect-time options a platform can contribute to the handshake. React Native has no
 * cookie jar, so the mobile app passes `auth: { token }` (the server's getHandshakeSessionId
 * checks auth.token BEFORE the jwtToken cookie) and `extraHeaders: { Origin }` (the namespace
 * middleware resolves the portal from the Origin header, which browsers send automatically).
 */
export type PortalChatSocketConnectOptions = {
  auth?: { token: string };
  extraHeaders?: Record<string, string>;
};

export type PortalChatSocketOptions = {
  /** Async so RN can read AsyncStorage; resolved once per connection attempt. Web omits it. */
  getConnectOptions?: () => Promise<PortalChatSocketConnectOptions>;
  /** Changing this value tears the socket down and reconnects (mobile portal switch). */
  reconnectKey?: string;
};

/**
 * One authenticated socket to the server's `/portal-chat` namespace.
 *
 * On web, auth rides on the session cookie: the API host is same-site, and the websocket-only
 * transport skips the polling/CORS path, so the `jwtToken` cookie flows on the upgrade request and
 * the namespace middleware validates it exactly like the HTTP `authorize` middleware. On mobile,
 * `getConnectOptions` supplies the Bearer token + Origin instead (see the type above).
 *
 * Handlers are kept in a ref so consumers can pass fresh closures every render without
 * reconnecting the socket.
 */
export function usePortalChatSocket(
  enabled: boolean,
  events: PortalChatSocketEvents,
  options?: PortalChatSocketOptions,
) {
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const socketRef = useRef<ReturnType<typeof io> | null>(null);
  const getConnectOptionsRef = useRef(options?.getConnectOptions);
  getConnectOptionsRef.current = options?.getConnectOptions;
  const reconnectKey = options?.reconnectKey;

  useEffect(() => {
    if (!enabled) return undefined;

    // Object property (not a let) so the cleanup's mutation is visible to the async closure's
    // control-flow analysis without a widening annotation.
    const lifecycle = { cancelled: false };
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let connectedSocket: ReturnType<typeof io> | null = null;

    void (async () => {
      let connectOptions: PortalChatSocketConnectOptions = {};
      const getConnectOptions = getConnectOptionsRef.current;
      if (getConnectOptions) {
        try {
          connectOptions = await getConnectOptions();
        } catch {
          // Missing platform credentials — connect anyway; the handshake will answer
          // "unauthorized" and the UI stays in its offline state.
        }
      }
      if (lifecycle.cancelled) return;

      const socket = io(`${getBackendUrl()}/portal-chat`, {
        transports: ["websocket"],
        withCredentials: true,
        // Dedicated Manager: without this, socket.io reuses one engine per origin and whichever of
        // this socket / the legacy Collaboration socket connects first silently dictates the
        // transports/withCredentials of the other.
        forceNew: true,
        ...connectOptions,
      });
      connectedSocket = socket;
      socketRef.current = socket;

      socket.on("connect", () => eventsRef.current.onConnectionChange?.(true));
      socket.on("disconnect", () => eventsRef.current.onConnectionChange?.(false));
      socket.on("connect_error", (error: Error) => {
        eventsRef.current.onConnectionChange?.(false);
        // A namespace-middleware denial is terminal for socket.io-client (no auto-reconnect). The
        // server names transient infra failures "retry"; retry those (and any other non-"unauthorized"
        // denial) with a slow manual reconnect so one Mongo/Redis blip at page load doesn't kill
        // realtime for the whole tab. Genuine "unauthorized" stays terminal.
        if (!socket.active && error.message !== "unauthorized") {
          clearTimeout(retryTimer);
          retryTimer = setTimeout(() => socket.connect(), SOCKET_RETRY_DELAY_MS);
        }
      });
      socket.on("message:new", (payload: MessagePayload) => eventsRef.current.onMessageNew?.(payload));
      socket.on("message:edited", (payload: MessagePayload) => eventsRef.current.onMessageEdited?.(payload));
      socket.on("message:deleted", (payload: MessageDeletedPayload) => eventsRef.current.onMessageDeleted?.(payload));
      socket.on("reaction:updated", (payload: ReactionPayload) => eventsRef.current.onReactionUpdated?.(payload));
      socket.on("read:updated", (payload: ReadPayload) => eventsRef.current.onReadUpdated?.(payload));
      socket.on("conversation:updated", (payload: ConversationPayload) =>
        eventsRef.current.onConversationUpdated?.(payload),
      );
      socket.on("member:removed", (payload: ConversationPayload) => eventsRef.current.onMemberRemoved?.(payload));
      socket.on("typing", (payload: TypingPayload) => eventsRef.current.onTyping?.(payload));
    })();

    return () => {
      lifecycle.cancelled = true;
      clearTimeout(retryTimer);
      socketRef.current = null;
      connectedSocket?.removeAllListeners();
      connectedSocket?.disconnect();
    };
  }, [enabled, reconnectKey]);

  const emitTyping = (conversationId: string, isTyping: boolean) => {
    // Identity is server-side (the authenticated socket) — no display name travels with the event.
    socketRef.current?.emit("typing", { conversationId, isTyping });
  };

  /**
   * Nudge a stalled connection — mobile calls this on AppState→active and NetInfo regain, where
   * the OS may have silently killed the websocket and socket.io's own retry back-off is mid-wait.
   * A no-op while connected or before the first connection attempt resolves.
   */
  const reconnectNow = useCallback(() => {
    const socket = socketRef.current;
    if (socket && !socket.connected) socket.connect();
  }, []);

  return { emitTyping, reconnectNow };
}
